// packages/shared/utils/date.js
// Date formatting and manipulation utilities shared across frontend and backend

/**
 * Format time remaining until event
 * @param {Date|string} eventStartTime
 * @returns {string} Human-readable time remaining
 */
function formatTimeUntilEvent(eventStartTime) {
  const now = new Date();
  const start = new Date(eventStartTime);
  const diff = start - now;

  if (diff < 0) return "Event has passed";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""}`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} ${minutes} min`;
  } else {
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  }
}

/**
 * Check if a date is within a reminder window
 * @param {Date|string} eventStartTime
 * @param {Date} windowStart
 * @param {Date} windowEnd
 * @returns {boolean}
 */
function isWithinReminderWindow(eventStartTime, windowStart, windowEnd) {
  const startTime = new Date(eventStartTime);
  return startTime >= windowStart && startTime <= windowEnd;
}

/**
 * Format date to human-readable string
 * @param {Date|string} dateString
 * @returns {string}
 */
function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format date and time
 * @param {Date|string} dateString
 * @returns {string}
 */
function formatDateTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 * @param {Date|string} dateString
 * @returns {string}
 */
function getRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? "s" : ""} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;

  return formatDate(dateString);
}

/**
 * Format time only (HH:MM AM/PM)
 * @param {Date|string} dateString
 * @returns {string}
 */
function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Check if a date is today
 * @param {Date|string} dateString
 * @returns {boolean}
 */
function isToday(dateString) {
  const date = new Date(dateString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Check if a date is in the future
 * @param {Date|string} dateString
 * @returns {boolean}
 */
function isFutureDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  return date > now;
}

module.exports = {
  formatTimeUntilEvent,
  isWithinReminderWindow,
  formatDate,
  formatDateTime,
  getRelativeTime,
  formatTime,
  isToday,
  isFutureDate
};
